// FeaturedHero.tsx — Phase 11 Step 3 Block 4
// Großformatiger Hero für das Scrolly-of-the-Day oberhalb des Feeds.
// Source-of-truth: Scrolly Platform.html:1119–1187 (`FeaturedHero`).
//
// TODO(content): `titleAccent` ist ein optionales Mock-Feld
// (`MockScrolly.titleAccent`). Wenn gesetzt und im Titel enthalten, wird
// dieses Wort kursiv+pink gerendert. Ohne Angabe fällt die Komponente auf
// das letzte Wort des Titels zurück (Quell-Verhalten aus Zeile 1150).
// Sobald das CMS/API ein eigenständiges `titleAccent`-Feld liefert, kann
// der Last-Word-Fallback entfernt werden.
//
// Abweichungen Briefing vs. Quelle (zusammengefasst):
//   - `padding` 40 (Briefing) statt 32/32 (Quelle).
//   - `gap` 44 (Briefing) statt 40 (Quelle).
//   - Container-Höhe 720 fix (Briefing) statt `minHeight: 380` (Quelle).
//   - CTA-Label "Scroll starten" (Briefing, dt.) statt "Start scroll" (Quelle).
// Diese Deltas sind in `HERO_STYLES` (tokens.ts) dokumentiert.

import type { CSSProperties, ReactNode } from "react";
import { Cover } from "./Cover";
import {
  IconCheckBadge,
  IconClock,
  IconEye,
  IconHeart,
  IconPlay,
  IconStar,
} from "../common/Icons";
import { useVariant } from "../../hooks/useVariant";
import {
  FONT_INSTRUMENT_SERIF,
  HERO_STYLES,
  PINK,
} from "../../lib/tokens";
import { formatViews } from "../../lib/format";
import { CATEGORY_BY_ID } from "../../lib/categories";
import { CREATORS_BY_HANDLE } from "../../lib/mock/creators";
import type { MockScrolly } from "../../lib/mock/scrollys";

// ---- Props --------------------------------------------------------------

export type FeaturedHeroProps = {
  scrolly: MockScrolly;
};

// ---- Component ----------------------------------------------------------

/**
 * FeaturedHero — "Scrolly of the Day" Hero oberhalb des Feeds.
 *
 * Liest `useVariant()` intern und picked `HERO_STYLES[variant]`. Keine
 * Farb-Token-Durchreichung über Props — analog zu ScrollyCard.
 *
 * Kein `onClick`-Behavior in Step 3 — Routing + CTA-Action werden in
 * Block 5 (Feed-Komposition) bzw. Step 4 (Viewer-Route) ergänzt.
 */
export function FeaturedHero({ scrolly }: FeaturedHeroProps) {
  const { variant } = useVariant();
  const style = HERO_STYLES[variant];

  const categoryLabel =
    CATEGORY_BY_ID[scrolly.categoryId]?.label ?? scrolly.categoryId;
  const creator = CREATORS_BY_HANDLE[scrolly.creator.handle];
  const followersLabel = creator?.followers ?? "";

  const viewsLabel = formatViews(scrolly.stats.views);
  const likesLabel = formatViews(scrolly.stats.likes);
  // Quell-Normalisierung analog ScrollyCard: "4 Min Scroll" → "4 Min".
  const durationLabel = scrolly.stats.duration.replace(/ Scroll$/i, "");

  const headingId = `hero-${scrolly.id}-title`;

  // Phase 11 Step 4 Block 2 — Routing:
  //   Hero-Cover + CTA verlinken zum Viewer, Creator-Chip zum Profil.
  const viewerHref = `/s/${scrolly.slug}`;
  const creatorHref = `/@${scrolly.creator.handle}`;

  return (
    <section
      aria-labelledby={headingId}
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr",
        gap: style.gap,
        alignItems: "center",
        padding: style.padding,
        width: "100%",
        maxWidth: style.maxWidth,
        height: style.height,
        background: style.surface,
        borderRadius: 20,
        boxShadow: style.shadow,
        border: style.border,
        boxSizing: "border-box",
      }}
    >
      {/* ---- Linke Spalte: Badge, Titel, Beschreibung, Creator + CTA, Meta ---- */}
      <div style={{ minWidth: 0 }}>
        <HeroBadge />

        <h2
          id={headingId}
          style={{
            fontFamily: FONT_INSTRUMENT_SERIF,
            // Briefing-Typo: 56 / lineHeight 1.0 / letterSpacing -1.2.
            // TODO(block-4-flag): bei 10+-Wort-Titeln wird die Höhe knapp —
            // Kandidat für `clamp(40px, 4.6vw, 56px)`. Aktuell nicht
            // implementiert, nur beobachtet (siehe Devview, Kategorie "lang").
            fontSize: 56,
            fontWeight: 500,
            color: style.text,
            margin: "20px 0 16px",
            letterSpacing: -1.2,
            lineHeight: 1.0,
          }}
        >
          <TitleWithAccent title={scrolly.title} accent={scrolly.titleAccent} />
        </h2>

        {scrolly.description && (
          <p
            style={{
              color: style.muted,
              fontSize: 16,
              lineHeight: 1.5,
              margin: 0,
              maxWidth: 520,
            }}
          >
            {scrolly.description}
          </p>
        )}

        {/* Creator + CTA — quell-treue Zeile aus HTML:1156–1173.
            Avatar + CheckBadge (CheckBadge immer rendern, nicht gated).
            CTA "Scroll starten" (dt., Briefing-Label). */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginTop: 24,
          }}
        >
          <a
            href={creatorHref}
            aria-label={`Profil von ${scrolly.creator.name}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              minWidth: 0,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <HeroAvatar
              name={scrolly.creator.name}
              color={scrolly.creator.avatar}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: style.text,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {scrolly.creator.name}
                </span>
                <IconCheckBadge size={12} />
              </div>
              <div style={{ fontSize: 12.5, color: style.muted }}>
                {followersLabel ? `${followersLabel} Follower · ` : ""}
                {scrolly.publishedAt}
              </div>
            </div>
          </a>
          <div style={{ flex: 1 }} />
          <a
            href={viewerHref}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: style.ctaBg,
              color: style.ctaText,
              padding: "11px 20px",
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              flexShrink: 0,
              textDecoration: "none",
            }}
          >
            <IconPlay size={14} /> Scroll starten
          </a>
        </div>

        {/* Meta-Row: Views · Duration · Likes (Briefing) */}
        <div
          style={{
            display: "flex",
            gap: 20,
            marginTop: 24,
            color: style.muted,
            fontSize: 13,
          }}
        >
          <MetaItem icon={<IconEye size={13} />}>
            {viewsLabel} Scrolls
          </MetaItem>
          <MetaItem icon={<IconClock size={13} />}>{durationLabel}</MetaItem>
          <MetaItem icon={<IconHeart size={13} />}>{likesLabel}</MetaItem>
        </div>
      </div>

      {/* ---- Rechte Spalte: großes Cover ---------------------------------
          Cover selbst verlinkt zum Viewer. Anchor ist Block-Display, damit
          der Container die 720-Höhe auch mit Link-Wrapper hält. */}
      <a
        href={viewerHref}
        aria-label={`${scrolly.title} — Scrolly öffnen`}
        style={{
          alignSelf: "stretch",
          borderRadius: style.coverRadius,
          overflow: "hidden",
          // Damit das 3/4-Cover in der festen 720-Höhe sauber sitzt.
          height: "100%",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          textDecoration: "none",
          color: "inherit",
        }}
      >
        <Cover
          palette={scrolly.cover.palette}
          decoration={scrolly.cover.decoration}
          category={categoryLabel}
          title={scrolly.title}
          creator={scrolly.creator}
          aspect={style.coverAspect}
          bigNumberValue={scrolly.cover.bigNumberValue}
        />
      </a>
    </section>
  );
}

// ---- Title-Split --------------------------------------------------------
// Wenn `accent` im Titel vorkommt, wird dieses Vorkommen kursiv+pink
// gerendert. Fallback: letztes Wort (Quell-Verhalten, HTML:1150).

function TitleWithAccent({
  title,
  accent,
}: {
  title: string;
  accent?: string;
}) {
  const emStyle: CSSProperties = {
    color: PINK,
    fontStyle: "italic",
    fontFamily: FONT_INSTRUMENT_SERIF,
  };

  // Fallback: last-word-Split (Quell-Verhalten).
  if (!accent) {
    const words = title.split(" ");
    if (words.length <= 1) {
      return <em style={emStyle}>{title}</em>;
    }
    const head = words.slice(0, -1).join(" ");
    const tail = words[words.length - 1];
    return (
      <>
        {head} <em style={emStyle}>{tail}</em>
      </>
    );
  }

  // titleAccent-Override: erstes Vorkommen ersetzen.
  const idx = title.indexOf(accent);
  if (idx < 0) {
    // Accent nicht im Titel gefunden → fallback auf last-word.
    // Defensiv, damit redaktioneller Tippfehler die Hero-Anzeige nicht bricht.
    const words = title.split(" ");
    const head = words.slice(0, -1).join(" ");
    const tail = words[words.length - 1];
    return (
      <>
        {head} <em style={emStyle}>{tail}</em>
      </>
    );
  }
  const before = title.slice(0, idx);
  const after = title.slice(idx + accent.length);
  return (
    <>
      {before}
      <em style={emStyle}>{accent}</em>
      {after}
    </>
  );
}

// ---- Badge --------------------------------------------------------------

function HeroBadge() {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: PINK,
        color: "#fff",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
    >
      <IconStar size={11} filled /> Scrolly of the Day
    </div>
  );
}

// ---- Meta-Item ----------------------------------------------------------

function MetaItem({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {icon}
      {children}
    </span>
  );
}

// ---- Avatar (Hero-größe) ------------------------------------------------
// Konsistenz mit ScrollyCard.Avatar, aber größere Default-Size (40) gemäß Quelle.

function HeroAvatar({
  name,
  color,
  size = 40,
}: {
  name: string;
  color: string;
  size?: number;
}) {
  const initial =
    name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color || PINK,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 600,
        fontSize: size * 0.42,
        flexShrink: 0,
        letterSpacing: -0.5,
        fontFamily: "inherit",
      }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}
